import { ChartNoAxesCombined, DollarSign, MapPin, Minus, Sparkles } from "lucide-react";
import React, { useEffect, useState } from "react";
import { getViewPatientData } from "../../lib";

const ECMAnalytics = ({ data, settings, bundle }) => {
    const [analytics, setAnalytics] = useState(null);

    useEffect(() => {
        if (data) {
            const computed = processECMData(data);
            console.log(computed, "===> computed ECM analytics");
            setAnalytics(computed);
        }
    }, [data]);

    const getLevelClass = (level) => {
        switch (level) {
            case "Critical High":
                return "label-danger";
            case "High":
                return "label-warning";
            case "Moderate":
                return "label-info";
            case "Low":
                return "label-success";
            default:
                return "label-default";
        }
    }

    const calculateClinicalRisk = (data) => {
        const CLINICAL_RULES = [
            { label: "CHF", points: 4, terms: ["chf", "heart failure", "congestive heart failure"] },
            { label: "Hypertension", points: 2, terms: ["hypertension", "hypertensive"] },
            { label: "Diabetes", points: 2, terms: ["diabetes", "dm2", "type 2 diabetes", "type ii diabetes"] },
            { label: "COPD", points: 3, terms: ["copd", "chronic obstructive pulmonary disease", "chronic obstructive lung disease"] },
            { label: "Asthma", points: 1, terms: ["asthma"] },
            { label: "CAD", points: 3, terms: ["cad", "coronary artery disease", "ischemic heart disease"] },
            { label: "CKD", points: 3, terms: ["ckd", "chronic kidney disease", "kidney disease", "renal disease"] }
        ];

        let totalScore = 0;
        let foundFactors = [];

        // Extract patient conditions
        const conditions = data
            ?.filter(e => e.resource.resourceType === "Condition")
            ?.map(e => e.resource.code?.text || e.resource.code?.coding?.[0]?.display || "")
            ?.filter(Boolean) || [];

        CLINICAL_RULES.forEach(rule => {
            const matched = conditions.some(cond => {
                const text = cond.toLowerCase();
                return rule.terms.some(term => text.includes(term));
            });
            if (matched) {
                foundFactors.push(rule.label);
                totalScore += rule.points;
            }
        });

        let scoreLabel = "Low";
        if (totalScore > 3 && totalScore <= 6) scoreLabel = "Moderate";
        else if (totalScore > 6 && totalScore <= 9) scoreLabel = "High";
        else if (totalScore > 9) scoreLabel = "Critical High";

        return { level: scoreLabel, keyFactors: foundFactors };
    };

    const calculateUtilizationRisk = (data) => {
        const now = new Date();
        const sixMonthsAgo = new Date();
        sixMonthsAgo.setMonth(now.getMonth() - 6);

        // Extract Encounter resources
        const encounters = data
            ?.filter(e => e.resource.resourceType === "Encounter")
            ?.map(e => e.resource) || [];

        let EDVisits = 0;
        let Admissions = 0;

        encounters.forEach(enc => {
            const typeText = enc.type?.[0]?.text || enc.type?.[0]?.coding?.[0]?.display || "";
            const cls = enc.class?.code || enc.class?.display || "";
            const start = new Date(enc.period?.start);

            // Only consider encounters within the last 6 months
            if (isNaN(start.getTime()) || start < sixMonthsAgo || start > now) return;

            // Count ED Visits
            if (/ED|Emergency/i.test(typeText) || /EMER/i.test(cls)) {
                EDVisits++;
            }

            // Count Admissions
            if (/inpatient|admission/i.test(typeText) || /IMP/i.test(cls)) {
                Admissions++;
            }
        });

        // ---- Determine utilization risk level ----
        let totalUtilization = EDVisits + Admissions;
        let level = "Low";

        if (totalUtilization >= 2 && totalUtilization < 4) level = "Moderate";
        else if (totalUtilization >= 4) level = "High";

        // ---- Build key factors ----
        const keyFactors = [];

        if (EDVisits > 0) keyFactors.push(`${EDVisits} ED Visit${EDVisits > 1 ? "s" : ""}`);
        if (Admissions > 0) keyFactors.push(`${Admissions} Hospital Admission${Admissions > 1 ? "s" : ""}`);

        // Detect frequent readmissions (≥2 admissions)
        if (Admissions >= 2) keyFactors.push("Frequent Readmissions");

        // If no encounters found, add a neutral key factor
        if (keyFactors.length === 0) keyFactors.push("No recent hospital utilization");

        return { level, EDVisits, Admissions, keyFactors };
    };

    const calculateSocialRisk = (data) => {
        const RISK_POINTS = {
            "Housing Instability": 3,
            "Food Insecurity": 3,
            "Transportation Insecurity": 2,
            "Financial Strain": 2,
            "Social Isolation": 1,
            "Unemployment": 2,
            "Utility Insecurity": 1
        };

        const sdohToRiskFactor = {
            "Food Security": "Food Insecurity",
            "Housing Stability": "Housing Instability",
            "Transportation": "Transportation Insecurity",
            "Financial Strain": "Financial Strain",
            "Social Support": "Social Isolation"
        };

        const supplementalMap = {
            "Unemployment": {
                icd10Prefixes: ["Z56.0", "Z56.2", "Z56.9"],
                textFallback: ["unemployment", "unemployed", "job loss"]
            },
            "Utility Insecurity": {
                icd10Prefixes: ["Z59.861", "Z59.12"],
                textFallback: ["utility insecurity", "utilities shutoff", "cannot pay utilities"]
            }
        };

        let totalScore = 0;
        const foundFactors = new Set();

        // Keep social risk consistent with SDoH statuses: only UNMET contributes to risk points.
        const sdohStatuses = determineSdohStatus(data);
        sdohStatuses.forEach(item => {
            if (item.status !== "UNMET") return;
            const mapped = sdohToRiskFactor[item.name];
            if (!mapped || !RISK_POINTS[mapped]) return;
            foundFactors.add(mapped);
            totalScore += RISK_POINTS[mapped];
        });

        const socialData = data?.filter(e =>
            ["Observation", "Condition", "QuestionnaireResponse"].includes(e.resource.resourceType)
        ) || [];
        const conditionCodes = new Set();
        const texts = [];

        socialData.forEach(e => {
            const res = e.resource;
            const resourceType = res.resourceType;

            if (resourceType === "Condition") {
                (res.code?.coding || []).forEach(c => {
                    if (c?.code) conditionCodes.add(String(c.code).toUpperCase());
                    if (c?.display) texts.push(String(c.display));
                });
                if (res.code?.text) texts.push(String(res.code.text));
            }

            if (resourceType === "Observation") {
                (res.code?.coding || []).forEach(c => {
                    if (c?.display) texts.push(String(c.display));
                });
                if (res.code?.text) texts.push(String(res.code.text));
                if (res.valueCodeableConcept?.text) texts.push(String(res.valueCodeableConcept.text));
                if (res.valueCodeableConcept?.coding?.[0]?.display) texts.push(String(res.valueCodeableConcept.coding[0].display));
                if (res.valueString) texts.push(String(res.valueString));
                if (res.text?.div) texts.push(String(res.text.div));
            }

            if (resourceType === "QuestionnaireResponse") {
                (res.item || []).forEach(item => {
                    (item.code || []).forEach(c => {
                        if (c?.display) texts.push(String(c.display));
                    });
                    (item.answer || []).forEach(ans => {
                        if (ans.valueCoding?.display) texts.push(String(ans.valueCoding.display));
                        if (ans.valueString) texts.push(String(ans.valueString));
                    });
                    if (item.text) texts.push(String(item.text));
                });
            }
        });

        Object.entries(supplementalMap).forEach(([factor, map]) => {
            if (foundFactors.has(factor)) return;

            const matchesIcd = Array.from(conditionCodes).some(code =>
                (map.icd10Prefixes || []).some(prefix => code.startsWith(String(prefix).toUpperCase()))
            );
            const matchesText = texts.some(txt => {
                const value = String(txt).toLowerCase();
                return (map.textFallback || []).some(term => value.includes(String(term).toLowerCase()));
            });

            if (matchesIcd || matchesText) {
                foundFactors.add(factor);
                totalScore += RISK_POINTS[factor];
            }
        });

        let level = "Low";
        if (totalScore > 2 && totalScore <= 4) level = "Moderate";
        else if (totalScore > 4 && totalScore <= 7) level = "High";
        else if (totalScore > 7) level = "Critical High";

        const keyFactors = Array.from(foundFactors);
        if (keyFactors.length === 0) {
            keyFactors.push("No major social risk factors detected");
        }

        return { level, keyFactors };
    };

    const calculateCareCoordinationRisk = (data) => {
        const CARE_FACTORS = [
            "Medication Nonadherence",
            "Care Plan Noncompliance",
            "Missed Appointments",
            "Refused Follow-up",
            "Poor Engagement",
            "Nonresponsive to Outreach",
            "Self-Discontinued Medication"
        ];

        const RISK_POINTS = {
            "Medication Nonadherence": 3,
            "Care Plan Noncompliance": 3,
            "Missed Appointments": 2,
            "Refused Follow-up": 2,
            "Poor Engagement": 2,
            "Nonresponsive to Outreach": 2,
            "Self-Discontinued Medication": 3
        };

        let totalScore = 0;
        let foundFactors = [];

        // Extract relevant FHIR data for behavior & adherence
        const careData = data?.filter(e =>
            ["CarePlan", "MedicationStatement", "MedicationRequest", "Observation", "Condition"].includes(e.resource.resourceType)
        ) || [];

        const texts = careData.map(e => {
            const res = e.resource;
            return (
                res.code?.text ||
                res.code?.coding?.[0]?.display ||
                res.valueCodeableConcept?.text ||
                res.valueCodeableConcept?.coding?.[0]?.display ||
                res.note?.[0]?.text ||
                res.statusReason?.text ||
                res.description ||
                ""
            );
        }).filter(Boolean);

        // Detect matching risk behaviors
        CARE_FACTORS.forEach(factor => {
            if (texts.some(txt => txt.toLowerCase().includes(factor.toLowerCase()))) {
                foundFactors.push(factor);
                totalScore += RISK_POINTS[factor];
            }
        });

        // Determine care coordination level
        let level = "Low";
        if (totalScore > 2 && totalScore <= 5) level = "Moderate";
        else if (totalScore > 5 && totalScore <= 8) level = "High";
        else if (totalScore > 8) level = "Critical High";

        if (foundFactors.length === 0) {
            foundFactors.push("Adherent to care plan and medication");
        }

        return { level, keyFactors: foundFactors };
    };

    const calculateZipcodeRisk = (data) => {
        // Example static risk map (you can later replace this with a real ADI dataset)
        const ZIP_RISK_MAP = {
            "90001": 92,
            "75216": 88,
            "02118": 55,
            "60614": 20,
            "19132": 70,
            "48205": 85,
            "10457": 80,
            "85009": 68
        };

        // Extract patient ZIP
        const patient = data?.find(e => e.resource.resourceType === "Patient")?.resource;
        const zip = patient?.address?.[0]?.postalCode;

        if (!zip) {
            return { score: 0, keyFactors: ["ZIP code not found"], zip: null };
        }

        // Lookup risk from dataset or default value
        const riskScore = ZIP_RISK_MAP[zip] || Math.floor(Math.random() * 30) + 30;
        // → fallback gives 30–60% if not found

        // Derive key factor text
        let keyFactor = "";
        if (riskScore >= 80) keyFactor = "Lives in high-deprivation area";
        else if (riskScore >= 50) keyFactor = "Lives in moderate-deprivation area";
        else keyFactor = "Lives in low-deprivation area";

        return { score: riskScore, keyFactors: [keyFactor], zip };
    };

    const calculateIncomeAndEducation = (data) => {
        let income = null;
        let education = null;
        const keyFactors = [];

        // Filter relevant resources
        const socialData = data?.filter(e =>
            ["Observation", "QuestionnaireResponse", "Patient"].includes(e.resource.resourceType)
        ) || [];

        socialData.forEach(e => {
            const res = e.resource;

            // 1️⃣ Annual Income
            if (!income) {
                // Example: Observation with valueQuantity or valueString
                if (res.code?.text?.toLowerCase().includes("income")) {
                    income = res.valueQuantity?.value || res.valueString || null;
                    if (income) keyFactors.push(`Annual Income: ${income}`);
                }
            }

            // 2️⃣ Education Level
            if (!education) {
                if (res.code?.text?.toLowerCase().includes("education") || res.code?.coding?.[0]?.display?.toLowerCase().includes("education")) {
                    education = res.valueCodeableConcept?.text || res.valueString || null;
                    if (education) keyFactors.push(`Education Level: ${education}`);
                }
            }
        });

        // Default if not found
        if (!income) keyFactors.push("Annual Income: Not provided");
        if (!education) keyFactors.push("Education Level: Not provided");

        return { income, education, keyFactors };
    };
    const determineSdohStatus = (fhirData) => {
        const factors = [
            {
                name: "Food Security",
                loincCodes: ["88121-9", "88122-7"],
                unmetAnswerCodes: ["LA30190-5", "LA30191-3"],
                metAnswerCodes: ["LA30189-7"],
                unmetIcdPrefix: ["Z59.4"],
                unmetText: ["food insecurity", "hungry", "not enough food"],
                metText: ["food secure", "no food insecurity"]
            },
            {
                name: "Housing Stability",
                loincCodes: ["71802-3"],
                unmetIcdPrefix: ["Z59.0", "Z59.1", "Z59.2"],
                unmetText: ["housing instability", "housing insecurity", "homeless", "unstable housing"],
                metText: ["stable housing", "housed"]
            },
            {
                name: "Transportation",
                loincCodes: ["93030-5"],
                unmetAnswerCodes: ["LA30190-5"],
                metAnswerCodes: ["LA30189-7"],
                unmetIcdPrefix: ["Z59.82"],
                unmetText: ["transportation insecurity", "no transportation", "transportation barrier"],
                metText: ["has transportation", "reliable transportation"]
            },
            {
                name: "Financial Strain",
                loincCodes: ["76513-1"],
                unmetAnswerCodes: ["LA30190-5"],
                metAnswerCodes: ["LA30189-7"],
                unmetIcdPrefix: ["Z59.6", "Z59.7"],
                unmetText: ["financial strain", "cannot pay", "financial insecurity"],
                metText: ["no financial strain", "financially stable"]
            },
            {
                name: "Social Support",
                unmetIcdPrefix: ["Z60", "Z63"],
                unmetText: ["social isolation", "limited social support", "lonely"],
                metText: ["adequate social support", "good social support"]
            }
        ];

        const inferStatusFromText = (text, factor) => {
            if (!text) return null;
            const value = String(text).toLowerCase();
            if ((factor.unmetText || []).some(term => value.includes(term))) return "UNMET";
            if ((factor.metText || []).some(term => value.includes(term))) return "MET";
            return null;
        };

        return factors.map(factor => {
            let status = "NOT SCREENED";
            let value = "";

            const observations = fhirData.filter(r =>
                r.resource.resourceType === "Observation" &&
                r.resource.code?.coding?.some(c => factor.loincCodes?.includes(c.code))
            );

            if (observations.length > 0) {
                status = "UNKNOWN";

                for (const obs of observations) {
                    const answerCoding = obs.resource.valueCodeableConcept?.coding?.[0];
                    const answerText =
                        obs.resource.valueCodeableConcept?.text ||
                        obs.resource.valueCodeableConcept?.coding?.[0]?.display ||
                        obs.resource.valueString ||
                        "";

                    if (answerCoding) {
                        value = answerCoding.display || answerText;
                        if (factor.unmetAnswerCodes?.includes(answerCoding.code)) {
                            status = "UNMET";
                            break;
                        }
                        if (factor.metAnswerCodes?.includes(answerCoding.code)) {
                            status = "MET";
                        }
                    }

                    if (answerText) {
                        value = answerText;
                        const inferred = inferStatusFromText(answerText, factor);
                        if (inferred === "UNMET") {
                            status = "UNMET";
                            break;
                        }
                        if (inferred === "MET") {
                            status = "MET";
                        }
                    }
                }
            }

            const questionnaires = fhirData.filter(r =>
                r.resource.resourceType === "QuestionnaireResponse"
            );

            for (const qr of questionnaires) {
                const items = qr.resource.item || [];

                for (const item of items) {
                    const questionCodes = item.code?.map(c => c.code) || [];
                    const isMatch = questionCodes.some(code => factor.loincCodes?.includes(code));
                    if (!isMatch) continue;

                    const answers = item.answer || [];
                    for (const ans of answers) {
                        const coding = ans.valueCoding;
                        const answerText = ans.valueString || coding?.display || "";

                        if (coding) {
                            value = coding.display || answerText;
                            if (factor.unmetAnswerCodes?.includes(coding.code)) {
                                status = "UNMET";
                                break;
                            }
                            if (factor.metAnswerCodes?.includes(coding.code)) {
                                status = "MET";
                            }
                        }

                        if (answerText) {
                            value = answerText;
                            const inferred = inferStatusFromText(answerText, factor);
                            if (inferred === "UNMET") {
                                status = "UNMET";
                                break;
                            }
                            if (inferred === "MET") {
                                status = "MET";
                            }
                        }
                    }
                }
            }

            if (factor.unmetIcdPrefix) {
                const conditions = fhirData.filter(r =>
                    r.resource.resourceType === "Condition" &&
                    r.resource.code?.coding?.some(c =>
                        factor.unmetIcdPrefix.some(prefix => String(c.code || "").startsWith(prefix))
                    )
                );

                if (conditions.length > 0) {
                    status = "UNMET";
                    value = conditions[0].resource.code.coding?.[0]?.display || "Condition indicates unmet need";
                }
            }

            return {
                name: factor.name,
                status,
                value: value || "No data available"
            };
        });
    };


    // const determineSdohStatus = (fhirData) => {
    //     // Define factors and mapping to FHIR codes (Observation.code or QuestionnaireResponse codes)
    //     const factors = [
    //         { name: "House Stability", codes: ["house-stability", "housing-status"], value: "Patient report housing Instability in the past 12 months" },
    //         { name: "Food Security", codes: ["food-security", "nutrition-insecurity"], value: "Screened positive for food insecurity" },
    //         { name: "Transportation", codes: ["transportation-access"], value: "Has reliable transportation to appointments" },
    //         { name: "Social Support", codes: ["social-support", "support-network"], value: "Limited social support network identified" },
    //         { name: "Financial Strain", codes: ["financial-strain", "income-challenges"], value: "Difficulty paying for things like medication, housing and basic needs" }
    //     ];
    //     return factors.map(factor => {
    //         // Find any matching FHIR observation/questionnaire for this factor
    //         const relatedResources = fhirData.filter(res =>
    //             res.resource.resourceType === "Observation" || res.resource.resourceType === "QuestionnaireResponse"
    //         ).filter(res =>
    //             factor.codes.includes(res.resource.code?.coding?.[0]?.code) || factor.codes.includes(res.resource.code?.text)
    //         );

    //         let status = "MET"; // default

    //         if (relatedResources.length > 0) {
    //             // For each matching resource, check the value to see if it's unmet
    //             for (const res of relatedResources) {
    //                 let value = "";
    //                 if (res.resource.valueString) value = res.resource.valueString;
    //                 else if (res.resource.valueCodeableConcept) value = res.resource.valueCodeableConcept.text || "";
    //                 else if (res.resource.item && Array.isArray(res.resource.item)) {
    //                     // QuestionnaireResponse
    //                     value = res.resource.item.map(i => i.answer?.map(a => a.valueString || a.valueCoding?.display).join(", ")).join("; ");
    //                 }

    //                 if (value) {
    //                     const valLower = value.toLowerCase();
    //                     switch (factor.name) {
    //                         case "House Stability":
    //                             if (valLower.includes("unstable") || valLower.includes("homeless")) status = "UNMET";
    //                             break;
    //                         case "Food Security":
    //                             if (valLower.includes("insecure") || valLower.includes("skip meals")) status = "UNMET";
    //                             break;
    //                         case "Transportation":
    //                             if (valLower.includes("unreliable") || valLower.includes("no access")) status = "UNMET";
    //                             break;
    //                         case "Social Support":
    //                             if (valLower.includes("limited") || valLower.includes("none")) status = "UNMET";
    //                             break;
    //                         case "Financial Strain":
    //                             if (valLower.includes("difficulty") || valLower.includes("cannot pay")) status = "UNMET";
    //                             break;
    //                     }
    //                 }
    //             }
    //         }

    //         return { name: factor.name, status, value: factor.value };
    //     });
    // };

    const calculateEcmProbabilities = (risks) => {
        const { clinicalRisk, utilizationRisk, socialRisk, careCoordination, zipcodeRisk } = risks;

        // Helper: Convert levels to numeric weights
        const levelToScore = (level) => {
            switch (level?.toLowerCase()) {
                case "low": return 20;
                case "moderate": return 50;
                case "high": return 75;
                case "critical high": return 90;
                default: return 0;
                // case "Low": return 20;
                // case "Moderate": return 50;
                // case "High": return 75;
                // case "Critical high": return 90;
                // default: return 0;
            }
        };

        // Base conversion probability (ECM eligibility)
        const clinicalScore = levelToScore(clinicalRisk.level);
        const socialScore = levelToScore(socialRisk.level);
        const utilizationScore = levelToScore(utilizationRisk.level);
        const careScore = levelToScore(careCoordination.level);
        const zipcodeScore = zipcodeRisk.score || 50;


        // Weighted formula (you can tweak these weights)
        const conversionProbability = Math.min(
            100,
            Math.round((clinicalScore * 0.3 + utilizationScore * 0.25 + socialScore * 0.2 + careScore * 0.15 + zipcodeScore * 0.1))
        );

        // Re-authorization Probability (depends on continued high risk + care coordination issues)
        const reauthProbability = Math.min(
            100,
            Math.round((clinicalScore * 0.25 + utilizationScore * 0.25 + careScore * 0.4 + socialScore * 0.1))
        );

        // Categorize levels for labels
        const getLevel = (score) => {
            if (score <= 30) return "Low";
            if (score <= 60) return "Moderate";
            if (score <= 85) return "High";
            return "Critical High";
        };

        return {
            ecmConversion: {
                percentage: conversionProbability,
                level: getLevel(conversionProbability),
            },
            reauthorization: {
                percentage: reauthProbability,
                level: getLevel(reauthProbability),
            },
        };
    };

    const processECMData = (bundle) => {
        if (!bundle) return null;
        // Extract key FHIR resources
        const patient = bundle.find(e => e.resource.resourceType === "Patient")?.resource;
        const conditions = bundle
            .filter(e => e.resource.resourceType === "Condition")
            .map(e => e.resource);
        const encounters = bundle
            .filter(e => e.resource.resourceType === "Encounter")
            .map(e => e.resource);

        // -------------------------
        // 3️⃣ Social Risk (basic inference)
        // -------------------------
        const address = patient?.address?.[0];
        const city = address?.city || "";
        const hasAddress = !!address?.line?.length;

        // -------------------------
        // 5️⃣ Combine results
        // -------------------------
        return {
            demographics: {
                name: `${patient?.name?.[0]?.given?.[0] || ""} ${patient?.name?.[0]?.family || ""}`,
                dob: patient?.birthDate || "N/A",
                gender: patient?.gender || "N/A",
                address: address
                    ? `${address?.line?.[0] || ""}, ${address?.city || ""}, ${address?.state || ""}`
                    : "N/A",
                additional: calculateIncomeAndEducation(bundle)
            },
            clinicalRisk: calculateClinicalRisk(bundle),
            utilizationRisk: calculateUtilizationRisk(bundle),
            socialRisk: calculateSocialRisk(bundle),
            careCoordination: calculateCareCoordinationRisk(bundle),
            zipcodeRisk: calculateZipcodeRisk(bundle),
            sdoh: determineSdohStatus(bundle)
        };
    };

    if (!analytics) {
        return (
            <div className="text-center" style={{ padding: "40px" }}>
                Loading ECM Analytics...
            </div>
        );
    }

    const { demographics, clinicalRisk, utilizationRisk, socialRisk, sdoh, careCoordination, zipcodeRisk } = analytics;

    const { ecmConversion, reauthorization } = calculateEcmProbabilities({
        clinicalRisk,
        utilizationRisk,
        socialRisk,
        careCoordination,
        zipcodeRisk
    });

    console.log("ECM Probabilities:", ecmConversion, reauthorization);

    return (
        <div className="container-fluid">

            <div className="row">
                <div className="col-sm-9">
                    {/* Risk Summary */}
                    <div className="row">
                        <div className="col-sm-12">
                            <div className="panel panel-default">
                                <div className="panel-body">
                                    <div>
                                        <h5 style={{ fontSize: "15px" }} className="text-primary">
                                            <strong className="d-flex align-items-center">
                                                <Sparkles size={14} style={{ marginRight: "5px" }} /> ECM Enrollment Prediction Summary
                                            </strong>
                                        </h5>
                                    </div>
                                    <p className="text-primary">Predictive analytics to assess patient eligibility and likelihood of ECM enrollment based on demographic, clinical, social, and hospital utilization risks.</p>
                                    <br />
                                    <div>
                                        <p className="d-flex justify-content-between align-items-center">
                                            <strong className="d-flex align-items-center">ECM Conversion Probability <ChartNoAxesCombined size={14} style={{ marginLeft: "5px" }} /></strong>
                                            <span className={`label ${getLevelClass(ecmConversion.level)}`} style={{ lineHeight: "normal", textTransform: "capitalize" }}>{ecmConversion.level} Probability</span>
                                        </p>
                                        <div className="progress">
                                            <div className="progress-bar" role="progressbar" style={{ width: `${ecmConversion.percentage}%` }} aria-valuenow={ecmConversion.percentage} aria-valuemin="0" aria-valuemax="100">{ecmConversion.percentage}%</div>
                                        </div>
                                    </div>
                                    <div>
                                        <p className="d-flex justify-content-between align-items-center">
                                            <strong className="d-flex align-items-center">Re-authorization Probability <Minus size={14} style={{ marginLeft: "5px" }} /></strong>
                                            <span className={`label ${getLevelClass(reauthorization.level)}`} style={{ lineHeight: "normal", textTransform: "capitalize" }}>{reauthorization.level} Probability</span>
                                        </p>
                                        <div className="progress">
                                            <div className="progress-bar" role="progressbar" style={{ width: `${reauthorization.percentage}%` }} aria-valuenow={reauthorization.percentage} aria-valuemin="0" aria-valuemax="100">{reauthorization.percentage}%</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-sm-6">
                            <div className="panel panel-default">
                                <div className="panel-body">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <h5 style={{ fontSize: "15px" }}>
                                            <strong>
                                                Clinical Risk Score
                                            </strong>
                                        </h5>
                                        <span className={`label ${getLevelClass(clinicalRisk.level)}`} style={{ lineHeight: "normal", textTransform: "capitalize" }}>{clinicalRisk.level}</span>
                                    </div>
                                    <br />
                                    <p style={{ color: "gray" }}>Based on chronic conditions, medications, and recent lab results</p>
                                    <br />
                                    <p><strong>Key Factors:</strong></p>
                                    <p>
                                        {clinicalRisk.keyFactors.length > 0 ? clinicalRisk.keyFactors.map((factor, index) => (
                                            <span key={index} className="label label-default" style={{ lineHeight: "normal", marginRight: "5px" }}>{factor}</span>
                                        )) : <span className="label label-default" style={{ lineHeight: "normal" }}>None</span>}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="col-sm-6">
                            <div className="panel panel-default">
                                <div className="panel-body">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <h5 style={{ fontSize: "15px" }}>
                                            <strong>
                                                Utilization Risk
                                            </strong>
                                        </h5>
                                        <span className={`label ${getLevelClass(utilizationRisk.level)}`} style={{ lineHeight: "normal", textTransform: "capitalize" }}>{utilizationRisk.level}</span>
                                    </div>
                                    <br />
                                    <p style={{ color: "gray" }}>ED visits and hospital admissions in the past 6 months</p>
                                    <br />
                                    <p><strong>Key Factors:</strong></p>
                                    <p>
                                        {utilizationRisk.keyFactors.length > 0 ? utilizationRisk.keyFactors.map((factor, index) => (
                                            <span key={index} className="label label-default" style={{ lineHeight: "normal", marginRight: "5px" }}>{factor}</span>
                                        )) : <span className="label label-default" style={{ lineHeight: "normal" }}>None</span>}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="col-sm-6">
                            <div className="panel panel-default">
                                <div className="panel-body">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <h5 style={{ fontSize: "15px" }}>
                                            <strong>
                                                Social Risk Score
                                            </strong>
                                        </h5>
                                        <span className={`label ${getLevelClass(socialRisk.level)}`} style={{ lineHeight: "normal", textTransform: "capitalize" }}>{socialRisk.level}</span>
                                    </div>
                                    <br />
                                    <p style={{ color: "gray" }}>Social determinants of health screening results</p>
                                    <br />
                                    <p><strong>Key Factors:</strong></p>
                                    <p>
                                        {socialRisk?.keyFactors.length > 0 ? socialRisk.keyFactors.map((factor, index) => (
                                            <span key={index} className="label label-default" style={{ lineHeight: "normal", marginRight: "5px" }}>{factor}</span>
                                        )) : <span className="label label-default" style={{ lineHeight: "normal" }}>None</span>}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="col-sm-6">
                            <div className="panel panel-default">
                                <div className="panel-body">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <h5 style={{ fontSize: "15px" }}>
                                            <strong>
                                                Care Coordination
                                            </strong>
                                        </h5>
                                        <span className={`label ${getLevelClass(careCoordination.level)}`} style={{ lineHeight: "normal", textTransform: "capitalize" }}>{careCoordination.level}</span>
                                    </div>
                                    <br />
                                    <p style={{ color: "gray" }}>Current patient behavior regarding care management and adherence</p>
                                    <br />
                                    <p><strong>Key Factors:</strong></p>
                                    <p>
                                        {careCoordination?.keyFactors.length > 0 ? careCoordination.keyFactors.map((factor, index) => (
                                            <span key={index} className="label label-default" style={{ lineHeight: "normal", marginRight: "5px" }}>{factor}</span>
                                        )) : <span className="label label-default" style={{ lineHeight: "normal" }}>None</span>}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-sm-3">
                    <div className="panel panel-default">
                        <div className="panel-body">
                            <div>
                                <div className="d-flex align-items-center">
                                    <MapPin size={16} />
                                    <h5 style={{ fontSize: "14px", marginLeft: "2px" }}>
                                        <strong>
                                            Geographic & Demographic Profile
                                        </strong>
                                    </h5>
                                </div>
                                <div className="row">
                                    <div className="col-sm-6">
                                        <strong>Location</strong>
                                        <p><small>{demographics.address}</small></p>
                                    </div>
                                    <div className="col-sm-6">
                                        <strong>Zipcode Risk Score</strong>
                                        <p><span className={`label label-warning`} style={{ lineHeight: "normal", textTransform: "capitalize" }}>{zipcodeRisk.score}/100</span></p>
                                    </div>
                                </div>
                            </div>
                            <hr />
                            <div>
                                <div className="d-flex align-items-center">
                                    <DollarSign size={16} />
                                    <h5 style={{ fontSize: "14px", marginLeft: "2px" }}>
                                        <strong>
                                            Socioeconomic Indicators
                                        </strong>
                                    </h5>
                                </div>
                                <div className="row">
                                    <div className="col-sm-6">
                                        <strong>Annual Income</strong>
                                        <p className="d-flex align-items-center">
                                            <small>
                                                {demographics.additional.income ? `$${demographics.additional.income}` : "Not provided"}
                                            </small>
                                        </p>
                                    </div>
                                    <div className="col-sm-6">
                                        <strong>Education Level</strong>
                                        <p>
                                            <small>
                                                {demographics.additional.education ? `${demographics.additional.education}` : "Not provided"}
                                            </small>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* SDoH Section */}
                    <div className="panel panel-default">
                        <div className="panel-body">
                            <div className="">
                                <h5 style={{ fontSize: "15px", marginBottom: "10px" }}>
                                    <strong>
                                        Social Determinants of Health (SDoH)
                                    </strong>
                                </h5>
                                {/* {(() => {
                                    const totalNeeds = sdoh.length;
                                    const unmetNeeds = sdoh.filter(item => item.status === "UNMET").length;
                                    return (
                                        <small>
                                            {unmetNeeds} of {totalNeeds} needs unmet
                                        </small>
                                    );
                                })()} */}
                            </div>
                            {sdoh.map((item, index) => {
                                return (
                                    <div key={item.name}>
                                        <div className="d-flex justify-content-between align-items-center">
                                            <h5 style={{ fontSize: "12px" }}>
                                                <strong>{item.name}</strong>
                                            </h5>
                                            <span
                                                className={`label ${item.status === "MET" ? "label-success" : "label-danger"}`}
                                                style={{ lineHeight: "normal", textTransform: "capitalize" }}
                                            >
                                                {item.status}
                                            </span>
                                        </div>
                                        <p style={{ color: "gray", fontSize: "12px" }}>{item.value}</p>
                                        {index !== sdoh.length - 1 && <hr />}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                </div>
            </div>


        </div>
    );
};

export default ECMAnalytics;
